import { type SelectionRange } from '../../../state/types.ts';
import { type SelectionAxis } from '../../types.ts';

import { getRangeMinMax } from './get-range-min-max.ts';

type IsIndexInRangeParams = {
  range: SelectionRange;
  index: number;
  axis: SelectionAxis;
};

type FindRangeContainingIndexParams = {
  ranges: SelectionRange[];
  index: number;
  axis: SelectionAxis;
};

/**
 * Checks if an index is within a selection range (inclusive) for the given axis.
 * @param range - The selection range to check
 * @param index - The index to check
 * @param axis - The axis to check ('row' or 'col')
 * @returns True if the index is within the range, false otherwise
 */
function isIndexInRange({ range, index, axis }: IsIndexInRangeParams): boolean {
  const { min, max } = getRangeMinMax(range, axis);
  return index >= min && index <= max;
}

/**
 * Finds the first selection range that contains the given index for the given axis.
 * Time Complexity: O(n) where n is the number of ranges
 * @param ranges - Array of selection ranges to search
 * @param index - The index to find
 * @param axis - The axis to search ('row' or 'col')
 * @returns The containing range, or undefined if not found
 */
export function findRangeContainingIndex({
  ranges,
  index,
  axis
}: FindRangeContainingIndexParams): SelectionRange | undefined {
  return ranges.find((range) => isIndexInRange({ range, index, axis }));
}
